import React, { useState, useRef, useCallback } from 'react';
import Paper from '@mui/material/Paper';
import { Tooltip } from '@mui/material';

const styles = {
  wrap: {
    width: '100%',
    border: '0.5px solid #d0d0d0',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  outerTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
    tableLayout: 'fixed',
  },
  outerThead: {
    backgroundColor: '#506C84',
    color: '#fff',
  },
  outerTh: {
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: 500,
    fontSize: '12px',
    color: '#fff',
    position: 'relative',
    userSelect: 'none',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  outerTd: {
    padding: '10px 12px',
    verticalAlign: 'middle',
    borderBottom: '0.5px solid #e0e0e0',
    fontSize: '13px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  arrowBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 4px',
    fontSize: '11px',
    color: '#666',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'transform 0.2s',
  },
  expandedTd: {
    padding: 0,
    backgroundColor: '#f5f7f9',
    borderBottom: '0.5px solid #e0e0e0',
  },
  instanceSection: {
    padding: '10px 16px 14px 16px',
  },
  sectionTitle: {
    fontSize: '11px',
    fontWeight: 500,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  innerTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px',
    border: '0.5px solid #d0d0d0',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  innerThead: {
    backgroundColor: '#E6F1FB',
  },
  innerTh: {
    padding: '7px 10px',
    textAlign: 'left',
    fontWeight: 500,
    color: '#185FA5',
    fontSize: '11px',
    borderBottom: '0.5px solid #b5d4f4',
  },
  innerTd: {
    padding: '8px 10px',
    verticalAlign: 'top',
    borderBottom: '0.5px solid #ebebeb',
    fontSize: '12px',
  },
  vmName: {
    fontWeight: 500,
    fontSize: '12px',
    color: '#222',
  },
  instanceId: {
    fontSize: '11px',
    color: '#888',
    marginTop: '2px',
  },
  badgeBlue: {
    display: 'inline-block',
    padding: '2px 7px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    backgroundColor: '#E6F1FB',
    color: '#185FA5',
  },
  badgeGreen: {
    display: 'inline-block',
    padding: '2px 7px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    backgroundColor: '#EAF3DE',
    color: '#3B6D11',
  },
  noData: {
    padding: '32px',
    textAlign: 'center',
    color: '#888',
    fontSize: '14px',
  },
  checkboxCell: {
    padding: '10px 8px',
    verticalAlign: 'middle',
    borderBottom: '0.5px solid #e0e0e0',
    width: '40px',
  },
  capacityCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  capacityMain: {
    fontWeight: 600,
    fontSize: '13px',
  },
  capacityMeta: {
    fontSize: '11px',
    color: '#888',
  },
  savingCost: {
    fontWeight: 500,
    color: '#1a7a3c',
  },
  // Resize handle
  resizeHandle: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '6px',
    cursor: 'col-resize',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resizeHandleInner: {
    width: '2px',
    height: '60%',
    borderRadius: '1px',
    backgroundColor: 'rgba(255,255,255,0.35)',
    transition: 'background-color 0.15s',
  },
  // Pagination styles
  paginationContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '10px 16px',
    borderTop: '0.5px solid #e0e0e0',
    gap: '8px',
    backgroundColor: '#fff',
    flexWrap: 'wrap',
  },
  paginationInfo: {
    fontSize: '13px',
    color: '#555',
    marginRight: '8px',
  },
  paginationBtn: {
    padding: '5px 12px',
    fontSize: '13px',
    border: '1px solid #c0cdd8',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#333',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  paginationBtnDisabled: {
    padding: '5px 12px',
    fontSize: '13px',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    backgroundColor: '#f5f5f5',
    color: '#bbb',
    cursor: 'not-allowed',
  },
  paginationBtnActive: {
    padding: '5px 12px',
    fontSize: '13px',
    border: '1px solid #506C84',
    borderRadius: '4px',
    backgroundColor: '#506C84',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600,
  },
  rowsPerPageSelect: {
    padding: '4px 8px',
    fontSize: '13px',
    border: '1px solid #c0cdd8',
    borderRadius: '4px',
    color: '#333',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Default column widths (px). Adjust as needed.
// Order matches the <th> order in the thead.
// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_COL_WIDTHS = [
  40,   // checkbox
  32,   // expand arrow
  60,   // ID
  160,  // Name
  110,  // Location
  140,  // Provisioning State
  130,  // SKU
  140,  // Resource Group
  130,  // Capacity
  80,   // OS
  90,   // Peak CPU
  120,  // Scaling Mode
  150,  // Orchestration Mode
  110,  // Saving Cost
  140,  // Created At
];

const MIN_COL_WIDTH = 40; // px

// ─────────────────────────────────────────────────────────────────────────────
// ResizableTh — a <th> with a drag handle on its right edge
// ─────────────────────────────────────────────────────────────────────────────
function ResizableTh({ width, onResize, style, children, noHandle }) {
  const [hovered, setHovered] = useState(false);
  const startXRef = useRef(null);
  const startWidthRef = useRef(null);

  const onMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      startXRef.current = e.clientX;
      startWidthRef.current = width;

      const onMouseMove = (moveEvent) => {
        const delta = moveEvent.clientX - startXRef.current;
        const newWidth = Math.max(MIN_COL_WIDTH, startWidthRef.current + delta);
        onResize(newWidth);
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [width, onResize]
  );

  return (
    <th
      style={{
        ...styles.outerTh,
        ...style,
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
      }}
    >
      {children}
      {!noHandle && (
        <div
          style={styles.resizeHandle}
          onMouseDown={onMouseDown}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div
            style={{
              ...styles.resizeHandleInner,
              backgroundColor: hovered
                ? 'rgba(255,255,255,0.85)'
                : 'rgba(255,255,255,0.35)',
            }}
          />
        </div>
      )}
    </th>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pagination Footer
// ─────────────────────────────────────────────────────────────────────────────
function PaginationFooter({
  currentPage,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) {
  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  const buildPageNumbers = () => {
    const pages = [];
    const delta = 1;
    const rangeStart = Math.max(2, currentPage - delta);
    const rangeEnd = Math.min(totalPages - 1, currentPage + delta);

    pages.push(1);
    if (rangeStart > 2) pages.push('...');
    for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
    if (rangeEnd < totalPages - 1) pages.push('...');
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  const startRow = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRow = Math.min(currentPage * pageSize, totalCount);

  return (
    <div style={styles.paginationContainer}>
      <span style={styles.paginationInfo}>Rows per page:</span>
      <select
        style={styles.rowsPerPageSelect}
        value={pageSize}
        onChange={(e) => onPageSizeChange(Number(e.target.value))}
      >
        {[10, 25, 50, 100].map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>

      <span style={{ ...styles.paginationInfo, marginLeft: '8px' }}>
        {startRow}–{endRow} of {totalCount}
      </span>

      <button
        style={currentPage === 1 ? styles.paginationBtnDisabled : styles.paginationBtn}
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        ‹ Prev
      </button>

      {buildPageNumbers().map((page, idx) =>
        page === '...' ? (
          <span key={`ellipsis-${idx}`} style={{ ...styles.paginationInfo, padding: '0 4px' }}>
            …
          </span>
        ) : (
          <button
            key={page}
            style={page === currentPage ? styles.paginationBtnActive : styles.paginationBtn}
            onClick={() => onPageChange(page)}
          >
            {page}
          </button>
        )
      )}

      <button
        style={currentPage === totalPages ? styles.paginationBtnDisabled : styles.paginationBtn}
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next ›
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Single expandable VMSS row
// ─────────────────────────────────────────────────────────────────────────────
function VMSSRow({ row, isSelected, onCheckboxChange, colWidths }) {
  const [expanded, setExpanded] = useState(false);
  const attachedVMs = row['attachedVMs'] ?? [];

  const tdStyle = (idx) => ({
    ...styles.outerTd,
    width: `${colWidths[idx]}px`,
    minWidth: `${colWidths[idx]}px`,
    maxWidth: `${colWidths[idx]}px`,
  });

  return (
    <>
      <tr style={{ backgroundColor: isSelected ? '#eaf3fb' : '#fff' }}>
        {/* Checkbox */}
        <td style={{ ...styles.checkboxCell, width: `${colWidths[0]}px` }}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onCheckboxChange(row, e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
        </td>

        {/* Expand arrow */}
        <td style={{ ...tdStyle(1), paddingRight: 0 }}>
          {attachedVMs.length > 0 && (
            <button
              style={{
                ...styles.arrowBtn,
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
              onClick={() => setExpanded((prev) => !prev)}
              title={expanded ? 'Collapse' : 'Expand attached VMs'}
            >
              ▶
            </button>
          )}
        </td>

        <td style={tdStyle(2)}>{row['id'] ?? '-'}</td>
        <td style={tdStyle(3)}>{row['vmssName'] ?? '-'}</td>
        <td style={tdStyle(4)}>{row['location'] ?? '-'}</td>

        <td style={tdStyle(5)}>
          <span style={row['provisioningState'] === 'Succeeded' ? styles.badgeGreen : styles.badgeBlue}>
            {row['provisioningState'] ?? '-'}
          </span>
        </td>

        <td style={tdStyle(6)}>{row['skuName'] ?? '-'}</td>
        <td style={tdStyle(7)}>{row['resourceGroup'] ?? '-'}</td>

        <td style={tdStyle(8)}>
          <Tooltip
            title={
              <>
                <div>Minimum: {row['minCapacity'] ?? 0}</div>
                <div>Maximum: {row['maxCapacity'] ?? 0}</div>
                <div>Default: {row['defaultCapacity'] ?? 0}</div>
              </>
            }
          >
            <div style={styles.capacityCell}>
              <span style={styles.capacityMain}>{row['currentCapacity'] ?? '-'}</span>
              <span style={styles.capacityMeta}>
                min {row['minCapacity'] ?? '-'} / max {row['maxCapacity'] ?? '-'}
              </span>
            </div>
          </Tooltip>
        </td>

        <td style={tdStyle(9)}>{row['osType'] ?? '-'}</td>

        <td style={tdStyle(10)}>
          {row['peakCpuValue'] != null ? `${row['peakCpuValue']}%` : '-'}
        </td>

        <td style={tdStyle(11)}>
          <span style={styles.badgeBlue}>{row['scalingMode'] ?? '-'}</span>
        </td>

        <td style={tdStyle(12)}>{row['orchestrationMode'] ?? '-'}</td>

        <td style={tdStyle(13)}>
          <Tooltip
            title={
              <>
                <div>Total Duration: {row['totalSavingCost']?.['totalDuration'] ?? 0} hrs</div>
              </>
            }
          >
            <span style={styles.savingCost}>
              ${(row['totalSavingCost']?.['totalCost'] ?? 0).toFixed(2)}
            </span>
          </Tooltip>
        </td>

        <td style={tdStyle(14)}>
          {row['timeCreated'] ?? '-'}
        </td>
      </tr>

      {/* Expanded attached VMs row */}
      {expanded && (
        <tr>
          <td colSpan={15} style={styles.expandedTd}>
            <div style={styles.instanceSection}>
              <div style={styles.sectionTitle}>Attached VMs ({attachedVMs.length})</div>
              <table style={styles.innerTable}>
                <thead style={styles.innerThead}>
                  <tr>
                    <th style={styles.innerTh}>VM Name</th>
                    <th style={styles.innerTh}>Instance ID</th>
                    <th style={styles.innerTh}>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {attachedVMs.map((vm, idx) => (
                    <tr
                      key={vm['instanceId'] ?? idx}
                      style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb' }}
                    >
                      <td style={styles.innerTd}>
                        <div style={styles.vmName}>{vm['vmName'] ?? '-'}</div>
                      </td>
                      <td style={styles.innerTd}>
                        <div style={styles.instanceId}>{vm['instanceId'] ?? '-'}</div>
                      </td>
                      <td style={styles.innerTd}>
                        <span style={styles.badgeBlue}>{vm['location'] ?? '-'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// VMSSInventoryTable — main export
// ─────────────────────────────────────────────────────────────────────────────
export default function VMSSInventoryTable({
  rows = [],
  selectedIDList = [],
  onCheckboxChange,
  totalCount = 0,
  pageSize = 25,
  currentPage = 1,
  onPageChange,
  onPageSizeChange,
}) {
  const [colWidths, setColWidths] = useState([...DEFAULT_COL_WIDTHS]);

  const handleResize = useCallback((colIdx, newWidth) => {
    setColWidths((prev) => {
      const next = [...prev];
      next[colIdx] = newWidth;
      return next;
    });
  }, []);

  const allSelected =
    rows.length > 0 && rows.every((r) => selectedIDList.includes(r['id']));

  const handleSelectAll = (e) => {
    rows.forEach((row) => onCheckboxChange(row, e.target.checked));
  };

  if (rows.length === 0) {
    return (
      <Paper style={{ width: '100%' }}>
        <div style={styles.wrap}>
          <div style={styles.noData}>No data found</div>
        </div>
      </Paper>
    );
  }

  const headers = [
    // [label, colIdx, noHandle?]
    [
      <input
        type="checkbox"
        checked={allSelected}
        onChange={handleSelectAll}
        style={{ cursor: 'pointer' }}
      />,
      0,
      true,
    ],
    ['', 1, true],
    ['ID', 2],
    ['Name', 3],
    ['Location', 4],
    ['Provisioning State', 5],
    ['SKU', 6],
    ['Resource Group', 7],
    ['Capacity', 8],
    ['OS', 9],
    ['Peak CPU', 10],
    ['Scaling Mode', 11],
    ['Orchestration Mode', 12],
    ['Saving Cost', 13],
    ['Created At', 14],
  ];

  return (
    <Paper style={{ width: '100%' }}>
      <div style={styles.wrap}>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.outerTable}>
            <thead style={styles.outerThead}>
              <tr>
                {headers.map(([label, idx, noHandle]) => (
                  <ResizableTh
                    key={idx}
                    width={colWidths[idx]}
                    onResize={(w) => handleResize(idx, w)}
                    noHandle={noHandle}
                  >
                    {label}
                  </ResizableTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <VMSSRow
                  key={row['id']}
                  row={row}
                  isSelected={selectedIDList.includes(row['id'])}
                  onCheckboxChange={onCheckboxChange}
                  colWidths={colWidths}
                />
              ))}
            </tbody>
          </table>
        </div>

        <PaginationFooter
          currentPage={currentPage}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={(newPage) => {
            if (typeof onPageChange === 'function') onPageChange(newPage);
          }}
          onPageSizeChange={(newSize) => {
            if (typeof onPageSizeChange === 'function') onPageSizeChange(newSize);
          }}
        />
      </div>
    </Paper>
  );
}